import SEO from '../components/SEO';
import './SimplePage.css';
import ParticleCanvas from '../components/ui/ParticleCanvas';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    content: `Account Information
When you create a Zepliq account, we collect your name, email address, and password.

WhatsApp Data
When you connect your WhatsApp Business account to Zepliq, we access and process:
• Your WhatsApp Business account information
• Incoming WhatsApp messages sent to your connected number
• Message metadata including timestamps and sender identifiers

Google Account Data (Gmail)
When you connect a Google account to use our Email Assistant, we access your Gmail data through Google APIs. See the "Google user data (Gmail)" section below for the exact scopes, how the data is used, and how to delete it.

Usage Data
We automatically collect information about how you use our platform, including pages visited, features used, and actions taken within the dashboard.`,
  },
  {
    title: '2. How We Use Your Information',
    content: `We use the information we collect to:
• Operate and provide the Zepliq platform and its AI features
• Process and respond to messages on behalf of your connected WhatsApp numbers and Google (Gmail) account using AI
• Store conversation history to provide context-aware AI responses
• Improve and develop our services
• Send you important service notifications and updates
• Respond to your support requests

AI Message Processing
Zepliq uses artificial intelligence (OpenAI's GPT models) to automatically generate replies to incoming messages on your connected accounts. Message content is sent to our AI provider solely for the purpose of generating a response. We do not use your message content to train AI models.`,
  },
  {
    title: 'Google user data (Gmail)',
    html: `<h2 class="legal-section__title">Google user data (Gmail)</h2>
<p>If you connect a Google account to use our Email Assistant, Zepliq accesses your
Google account via Google OAuth using these scopes:</p>
<ul>
  <li><code>.../auth/gmail.readonly</code> — read your email messages so the assistant understands incoming email.</li>
  <li><code>.../auth/gmail.modify</code> — organize messages (e.g. mark as read) on your behalf.</li>
  <li><code>.../auth/gmail.compose</code> — create draft replies for your review.</li>
  <li><code>.../auth/gmail.send</code> — send replies you approve, on your behalf.</li>
  <li><code>.../auth/userinfo.email</code>, <code>userinfo.profile</code> — identify the connected account.</li>
</ul>
<p><strong>Use:</strong> Google user data is used only to provide the user-facing email
features above. <strong>Sharing:</strong> email content is sent to our AI provider (OpenAI)
solely to generate the reply; it is not used to train models. We never sell it or use it for ads.</p>
<p><strong>Retention &amp; deletion:</strong> Disconnect Google in the Zepliq dashboard (Email page)
or revoke at <a href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</a>;
this deletes your stored Google tokens and Gmail data from our systems. Email
support@zepliq.com to delete your account.</p>`,
  },
  {
    title: 'Limited Use of Google user data',
    html: `<h2 class="legal-section__title">Limited Use of Google user data</h2>
<p>Zepliq's use and transfer of information received from Google APIs will adhere to the
<a href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</a>,
including the Limited Use requirements. We do not use Google user data to develop, improve, or train
generalized or non-personalized AI/ML models, and we do not transfer or use it for advertising.</p>`,
  },
  {
    title: '3. How We Store Your Data',
    content: `• All data is stored securely in our database (MongoDB)
• We retain active conversation data for as long as your account is active and connected
• When you disconnect a WhatsApp number or Google account, associated conversation data is removed from active processing
• We implement industry-standard security measures to protect your data`,
  },
  {
    title: '4. Data Sharing and Third Parties',
    content: `We do not sell, rent, or share your personal data or message content with third parties for advertising or marketing purposes.

We share data only with:
• OpenAI — message and email content is processed to generate AI replies (governed by OpenAI's privacy policy). It is not used to train models.
• Infrastructure providers — we use Railway and other cloud infrastructure providers to host our platform. These providers do not have access to your message content.`,
  },
  {
    title: '5. User Rights',
    content: `You have the right to:
• Access the personal data we hold about you
• Request correction of inaccurate data
• Request deletion of your account and associated data
• Disconnect any connected account (WhatsApp or Google) at any time
• Request a copy of your data

To exercise any of these rights, contact us at: support@zepliq.com`,
  },
  {
    id: 'delete',
    title: '6. Data Deletion',
    content: `To request deletion of your data:
• You can disconnect your accounts and delete your Zepliq account from the dashboard settings
• You can disconnect Google from the Email page in your dashboard, or revoke access at https://myaccount.google.com/permissions, which deletes your stored Google tokens and Gmail data from our systems
• You can submit a data deletion request at https://zepliq.com/privacy#delete or by emailing support@zepliq.com
• We will process deletion requests within 30 days`,
  },
  {
    title: '7. Cookies',
    content: `Zepliq uses essential cookies to maintain your login session and provide core platform functionality. We do not use advertising or tracking cookies.`,
  },
  {
    title: '8. Children\'s Privacy',
    content: `Zepliq is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children.`,
  },
  {
    title: '9. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a notice on our platform. The "Last updated" date at the top of this page reflects the most recent revision.`,
  },
  {
    title: '10. Contact Us',
    content: `If you have any questions about this Privacy Policy or how we handle your data, please contact us:

Zepliq
Email: support@zepliq.com
Website: https://zepliq.com`,
  },
];

export default function PrivacyPage() {
  return (
    <div className="simple-page">
      <SEO
        title="Privacy Policy — Zepliq"
        description="Read the Zepliq Privacy Policy. Learn how we collect, use, and protect your data across WhatsApp and Google (Gmail) integrations, including our Google API Limited Use disclosure."
        canonical="/privacy"
        noIndex={true}
      />

      <section className="simple-hero simple-hero--sm">
        <ParticleCanvas className="simple-hero__canvas" />
        <div className="simple-hero__blob simple-hero__blob--1" aria-hidden="true" />
        <div className="simple-hero__blob simple-hero__blob--2" aria-hidden="true" />
        <div className="container simple-hero__inner">
          <span className="badge badge-brand">Legal</span>
          <h1 className="display-xl simple-hero__title">Privacy Policy</h1>
          <p className="text-lg simple-hero__sub">
            Last updated: July 28, 2026
          </p>
        </div>
      </section>

      <section className="simple-section section">
        <div className="container">
          <div className="legal-intro">
            <p className="text-lg">
              This Privacy Policy describes how Zepliq ("we", "us", or "our") collects, uses, and protects your information when you use our platform at zepliq.com and its associated services.
            </p>
          </div>

          <div className="legal-body">
            {SECTIONS.map(s => (
              <div key={s.title} id={s.id} className="legal-section">
                {s.html ? (
                  <div
                    className="legal-section__content"
                    dangerouslySetInnerHTML={{ __html: s.html }}
                  />
                ) : (
                  <>
                    <h2 className="legal-section__title">{s.title}</h2>
                    <div className="legal-section__content">
                      {s.content.split('\n').map((line, i) => (
                        line.trim() ? <p key={i}>{line}</p> : <br key={i} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
